import { useState, useEffect } from 'react'
import { useLibraryStore } from '@/stores/library-store'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Loader2 } from 'lucide-react'
import {
  LIBRARY_FOLDER_CATEGORY_LABELS,
  type LibraryFolderCategory,
  type LibraryFolder,
} from '@/lib/schemas'

interface ChangeFolderCategoryDialogProps {
  folder: LibraryFolder
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ChangeFolderCategoryDialog({
  folder,
  open,
  onOpenChange,
}: ChangeFolderCategoryDialogProps) {
  const { updateFolder } = useLibraryStore()
  const [category, setCategory] = useState<LibraryFolderCategory>(folder.category)
  const [isSaving, setIsSaving] = useState(false)

  // Reset category when dialog opens
  useEffect(() => {
    if (open) {
      setCategory(folder.category)
    }
  }, [open, folder.category])

  const handleSave = async () => {
    if (category === folder.category) return

    setIsSaving(true)
    try {
      await updateFolder(folder.id, { category })
      onOpenChange(false)
    } catch {
      // Error handled in store
    } finally {
      setIsSaving(false)
    }
  }

  const categoryOptions = Object.entries(LIBRARY_FOLDER_CATEGORY_LABELS) as [
    LibraryFolderCategory,
    string
  ][]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Change Category</DialogTitle>
          <DialogDescription>
            Change the category for "{folder.name}". Documents in this folder
            will use this category to improve AI search results.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="space-y-2">
            <Label htmlFor="folder-category">Category</Label>
            <Select
              value={category}
              onValueChange={(v) => setCategory(v as LibraryFolderCategory)}
              disabled={isSaving}
            >
              <SelectTrigger id="folder-category">
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {categoryOptions.map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={category === folder.category || isSaving}
          >
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
